import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Tabs,
  Theme,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import {
  BusinessOutlined,
  ExpandLess,
  ExpandMore,
  LocalShippingOutlined,
  LocationOnOutlined,
  PhoneOutlined,
} from "@mui/icons-material";
import { observer } from "mobx-react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  EmployeeAddress,
  HrmsEmployee,
  HrmsEmployeeListParams,
  HrmsEmployeeSortField,
} from "../../../../../models/Employees/HrmsEmployee";
import { SortType } from "../../../../../models/SortType";
import { FilterValue } from "../../../../../models/Filters/FilterValue";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import AppTablePagenation from "../../../../../views/components/common/pages/AppTablePagenation";
import { formatDateWithoutTime } from "../../../../../views/components/common/functions/formatDate";
import FormTextField from "../../../../../views/components/common/form/FormTextField";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import EmailCopyRow from "../../../../../views/components/common/EmailCopyRow";
import AppSearchDropdownMulti from "../../../../../views/components/common/filter/AppSearchDropdownMulti";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import PermissionBlockLabel from "../../../../../views/components/common/pop-up/PermissionBlockLabel ";
import { useDebounce } from "../../../../../views/components/common/hooks/Debounce";
import { listingStatusTabsStyles } from "../../../../../styles/listingStatusTabsSx";
import { useStores } from "../../../use-stores";

type HrmsStatusTab = "ACTIVE" | "INACTIVE" | "NEW";
type ChipTone = "success" | "danger" | "info" | "violet" | "neutral";

const DEFAULT_SORT_BY: HrmsEmployeeSortField = "record_created_date";
const DEFAULT_SORT_TYPE = SortType.DESC;
const DEFAULT_NEW_DAYS = 30;

const HRMS_STATUS_TABS: { id: HrmsStatusTab; label: string }[] = [
  { id: "ACTIVE", label: "Active" },
  { id: "INACTIVE", label: "Inactive" },
  { id: "NEW", label: "New" },
];

const NEW_DAY_OPTIONS: FilterValue[] = [
  { id: "30", value: "Last 30 days" },
  { id: "60", value: "Last 60 days" },
  { id: "90", value: "Last 90 days" },
  { id: "120", value: "Last 120 days" },
];

const SORT_FIELD_LABELS: Record<HrmsEmployeeSortField, string> = {
  record_created_date: "Created On",
  start_date: "Start Date",
  termination_date: "Termination Date",
};

const SORT_ORDER_OPTIONS: FilterValue[] = [
  { id: SortType.DESC, value: "Newest first" },
  { id: SortType.ASC, value: "Oldest first" },
];

const SHIPMENT_OPTIONS: FilterValue[] = [
  { id: "all", value: "All" },
  { id: "yes", value: "Has shipment" },
  { id: "no", value: "No shipment" },
];

/** `#52 · Bday · Swageazy` so campaigns that share a name stay distinct. */
const formatCampaignLabel = (
  id: number | string,
  name: string,
  customerName?: string | null,
) => {
  const label = `#${id} · ${name}`;
  return customerName ? `${label} · ${customerName}` : label;
};

const isSortField = (value: string | null): value is HrmsEmployeeSortField =>
  !!value && value in SORT_FIELD_LABELS;

/** Selected options stay at the top of a dropdown so the current choice is easy to see. */
const pinSelectedFirst = (
  options: FilterValue[],
  selected: FilterValue[],
): FilterValue[] => {
  if (selected.length === 0) return options;
  const selectedIds = new Set(selected.map((item) => String(item.id)));
  const pinned = selected.map(
    (item) =>
      options.find((option) => String(option.id) === String(item.id)) ?? item,
  );
  const rest = options.filter((option) => !selectedIds.has(String(option.id)));
  return [...pinned, ...rest];
};

/** Termination date only means something for inactive employees. */
const getSortFieldsForTab = (tab: HrmsStatusTab): HrmsEmployeeSortField[] =>
  tab === "INACTIVE"
    ? ["record_created_date", "start_date", "termination_date"]
    : ["record_created_date", "start_date"];

const getStatusParams = (
  tab: HrmsStatusTab,
  newDays?: number,
): Partial<HrmsEmployeeListParams> => {
  if (tab === "INACTIVE") {
    return {
      employment_status: "INACTIVE",
      active: undefined,
      inactive: true,
      new_employees: undefined,
      new_days: undefined,
    };
  }
  if (tab === "NEW") {
    return {
      employment_status: undefined,
      active: undefined,
      inactive: undefined,
      new_employees: true,
      new_days: newDays,
    };
  }
  return {
    employment_status: "ACTIVE",
    active: true,
    inactive: undefined,
    new_employees: undefined,
    new_days: undefined,
  };
};

/** Theme-aware surfaces per the admin theme guide (customColors stay light in dark mode). */
const getHrmsColors = (theme: Theme) => {
  const c = theme.palette.customColors;
  const isDark = theme.palette.mode === "dark";
  return {
    isDark,
    surface: isDark ? "#1E293B" : c.white,
    softSurface: isDark ? "#1E293B" : c.softCardBackground,
    headerBg: isDark ? "#334155" : c.lightGreyColor,
    border: isDark ? "#334155" : c.lightGray,
    softBorder: isDark ? "#334155" : c.softBorder,
    title: isDark ? "#F1F5F9" : c.darkBlueText,
    subtitle: isDark ? "#94A3B8" : c.grayText,
    label: isDark ? "#94A3B8" : c.mutedSlateText,
    icon: isDark ? "#94A3B8" : c.mediumGray,
    rowHover: isDark ? "rgba(255,255,255,0.06)" : c.blueLightBackground,
    accent: c.purple,
  };
};

const getChipTone = (theme: Theme, tone: ChipTone) => {
  const c = theme.palette.customColors;
  const isDark = theme.palette.mode === "dark";
  const tones: Record<ChipTone, { fg: string; bg: string; border: string }> = {
    success: {
      fg: c.successGreen,
      bg: c.successGreenLightBackground,
      border: c.successGreenLightBorder,
    },
    danger: {
      fg: c.errorRed,
      bg: theme.palette.blushPink.main,
      border: c.borderErrorRedColor,
    },
    info: { fg: c.darkBlue, bg: c.blueLightBackground, border: c.softBorder },
    violet: {
      fg: c.purpleMain,
      bg: c.lightPurple,
      border: `${c.purpleMain}33`,
    },
    neutral: {
      fg: isDark ? "#94A3B8" : c.grayText,
      bg: c.veryLightGray,
      border: c.lightGray,
    },
  };
  const t = tones[tone];
  return isDark ? { fg: t.fg, bg: `${t.fg}1A`, border: `${t.fg}33` } : t;
};

const pillSx = (theme: Theme, tone: ChipTone) => {
  const t = getChipTone(theme, tone);
  return {
    height: 24,
    fontSize: "0.7rem",
    fontWeight: 600,
    borderRadius: "999px",
    color: t.fg,
    backgroundColor: t.bg,
    border: `1px solid ${t.border}`,
    "& .MuiChip-label": { px: 1.1 },
  };
};

const getDisplayName = (employee: HrmsEmployee) =>
  `${employee.first_name || ""} ${employee.last_name || ""}`.trim() || "-";

const getPhoneNumber = (employee: HrmsEmployee) =>
  employee.phone_numbers?.[0]?.number || "-";

const pickAddress = (
  addresses?: EmployeeAddress[] | null,
): EmployeeAddress | null => {
  if (!addresses?.length) return null;
  return (
    addresses.find((address) => address.address_type === "HOME_PRESENT") ||
    addresses.find((address) => address.address_type === "HOME_PERMANENT") ||
    addresses[0]
  );
};

const formatAddress = (address?: EmployeeAddress | null): string => {
  if (!address) return "-";
  const parts = [
    address.address_line1,
    address.address_line2,
    address.city,
    address.state,
    address.country,
  ]
    .filter((value) => value && String(value).trim() !== "")
    .map((value) => String(value).trim());
  return parts.length ? parts.join(", ") : "-";
};

const getPincode = (employee: HrmsEmployee) =>
  employee.custom_fields?.pincode ||
  pickAddress(employee.addresses)?.zip_code ||
  null;

const getProfileFields = (employee: HrmsEmployee) => {
  const cf = employee.custom_fields;
  return [
    { label: "T-shirt", value: cf?.tshirt_size },
    { label: "Mobile", value: cf?.employee_mobile_number },
    { label: "Group Co.", value: cf?.group_company },
    { label: "Class", value: cf?.employee_class },
    { label: "Assignment", value: cf?.assignment_class },
    { label: "HR Status", value: cf?.employee_status },
  ];
};

const isShipmentCreated = (employee: HrmsEmployee) =>
  Number(employee.shipment_created) === 1;

const getStatusTone = (status?: string | null): ChipTone =>
  status === "ACTIVE"
    ? "success"
    : status === "INACTIVE"
      ? "danger"
      : "neutral";

const HrmsList: React.FC = () => {
  const { hrmsStore, userStore, customerStore } = useStores();
  const theme = useTheme();
  const colors = getHrmsColors(theme);
  const isTableLayout = useMediaQuery(theme.breakpoints.up("lg"));
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);

  const resolvedCustomerId = (() => {
    const parsed = parseInt(searchParams.get("customer_id") || "", 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  })();
  const resolvedCampaignId = (() => {
    const parsed = parseInt(searchParams.get("campaign_id") || "", 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  })();
  const initialHasShipment = ((): boolean | undefined => {
    const raw = searchParams.get("has_shipment");
    if (raw === "true") return true;
    if (raw === "false") return false;
    return undefined;
  })();

  const initialStatusTab = ((): HrmsStatusTab => {
    if (searchParams.get("new_employees") === "true") return "NEW";
    if (
      searchParams.get("employment_status") === "INACTIVE" ||
      searchParams.get("inactive") === "true"
    ) {
      return "INACTIVE";
    }
    return "ACTIVE";
  })();

  const initialSortBy = ((): HrmsEmployeeSortField => {
    const raw = searchParams.get("sort_by");
    return isSortField(raw) &&
      getSortFieldsForTab(initialStatusTab).includes(raw)
      ? raw
      : DEFAULT_SORT_BY;
  })();
  const initialSortType =
    searchParams.get("sort_type") === SortType.ASC
      ? SortType.ASC
      : DEFAULT_SORT_TYPE;

  const [params, setParams] = useState<HrmsEmployeeListParams>(() => {
    const newDays = parseInt(searchParams.get("new_days") || "", 10);
    const contactNumber = (searchParams.get("contact_number") || "").trim();
    return {
      customer_id: resolvedCustomerId,
      page_no: parseInt(searchParams.get("page_no") || "1", 10) || 1,
      limit: parseInt(searchParams.get("limit") || "30", 10) || 30,
      search_text: searchParams.get("search_text") || "",
      contact_number: contactNumber || undefined,
      has_shipment: initialHasShipment,
      campaign_id: resolvedCampaignId || undefined,
      sort_by: initialSortBy,
      sort_type: initialSortType,
      ...getStatusParams(
        initialStatusTab,
        Number.isFinite(newDays) ? newDays : undefined,
      ),
    };
  });
  const [selectedStatusTab, setSelectedStatusTab] =
    useState<HrmsStatusTab>(initialStatusTab);
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(true);
  const [selectedCustomers, setSelectedCustomers] = useState<FilterValue[]>(
    resolvedCustomerId
      ? [
          {
            id: String(resolvedCustomerId),
            value: `Customer #${resolvedCustomerId}`,
          },
        ]
      : [],
  );
  const [customerQuery, setCustomerQuery] = useState("");
  const debouncedCustomerQuery = useDebounce(customerQuery, 400);
  const [customerOptions, setCustomerOptions] = useState<FilterValue[]>([]);
  const [customerError, setCustomerError] = useState<string | null>(null);

  if (hrmsStore.listSessionKey !== location.key) {
    hrmsStore.beginListFilters(location.key, {
      searchText: searchParams.get("search_text") || "",
      contactNumber: searchParams.get("contact_number") || "",
      selectedCampaigns: resolvedCampaignId
        ? [
            {
              id: String(resolvedCampaignId),
              value: `Campaign #${resolvedCampaignId}`,
            },
          ]
        : [],
    });
  }

  const canViewHrms = userStore.hasPermission([EmployeeRoles.VIEW_HRMS]);
  const sortFields = getSortFieldsForTab(selectedStatusTab);
  const sortFieldOptions: FilterValue[] = sortFields.map((field) => ({
    id: field,
    value: SORT_FIELD_LABELS[field],
  }));
  const draftCustomerId = (() => {
    const parsed = parseInt(String(selectedCustomers[0]?.id ?? ""), 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  })();
  const selectedShipment = SHIPMENT_OPTIONS.filter((option) =>
    params.has_shipment === true
      ? option.id === "yes"
      : params.has_shipment === false
        ? option.id === "no"
        : option.id === "all",
  );
  const selectedSortBy = sortFieldOptions.filter((option) => option.id === params.sort_by);
  const selectedSortOrder = SORT_ORDER_OPTIONS.filter(
    (option) => option.id === params.sort_type,
  );
  const selectedNewDays = NEW_DAY_OPTIONS.filter(
    (option) => option.id === String(params.new_days ?? DEFAULT_NEW_DAYS),
  );

  useEffect(() => {
    if (!canViewHrms) return;
    hrmsStore.getEmployees(params).catch(() => {
      // Error message is surfaced inline from the store.
    });
  }, [hrmsStore, params, canViewHrms]);

  useEffect(() => {
    if (!canViewHrms) return;
    hrmsStore
      .getEmployeeCounts({
        customer_id: params.customer_id,
        search_text: params.search_text,
        contact_number: params.contact_number,
        has_shipment: params.has_shipment,
        campaign_id: params.campaign_id,
        new_days: params.new_days ?? DEFAULT_NEW_DAYS,
      })
      .catch(() => {
        // Keep list experience functional even if counts endpoint fails.
      });
  }, [
    hrmsStore,
    canViewHrms,
    params.customer_id,
    params.search_text,
    params.contact_number,
    params.has_shipment,
    params.campaign_id,
    params.new_days,
  ]);

  useEffect(() => {
    const next = new URLSearchParams();
    if (params.customer_id > 0)
      next.set("customer_id", String(params.customer_id));
    next.set("page_no", String(params.page_no));
    next.set("limit", String(params.limit));
    if (params.search_text) next.set("search_text", params.search_text);
    if (params.contact_number) next.set("contact_number", params.contact_number);
    if (params.has_shipment === true) next.set("has_shipment", "true");
    if (params.has_shipment === false) next.set("has_shipment", "false");
    if (params.campaign_id && params.campaign_id > 0) {
      next.set("campaign_id", String(params.campaign_id));
    }
    if (params.employment_status) {
      next.set("employment_status", params.employment_status);
    }
    if (params.active === true) next.set("active", "true");
    if (params.inactive === true) next.set("inactive", "true");
    if (params.new_employees === true) next.set("new_employees", "true");
    if (params.new_days !== undefined)
      next.set("new_days", String(params.new_days));
    if (params.sort_by) next.set("sort_by", params.sort_by);
    if (params.sort_type) next.set("sort_type", params.sort_type);
    navigate({ search: next.toString() }, { replace: true });
  }, [params, navigate]);

  useEffect(() => () => hrmsStore.clearFilterTimers(), [hrmsStore]);

  const fetchCustomers = useCallback(
    async (searchText: string) => {
      const requestId = hrmsStore.nextCustomerSearchRequestId();
      try {
        setCustomerError(null);
        await customerStore.searchCustomers({
          page_no: 1,
          limit: 20,
          search_text: searchText || undefined,
        });
        if (!hrmsStore.isCurrentCustomerSearch(requestId)) return;
        setCustomerOptions(
          (customerStore.customers ?? [])
            .filter((customer) => customer.id != null)
            .map((customer) => ({
              id: String(customer.id),
              value: customer.name || String(customer.id),
            })),
        );
      } catch (error) {
        if (!hrmsStore.isCurrentCustomerSearch(requestId)) return;
        setCustomerOptions([]);
        setCustomerError("Failed to search customers.");
      }
    },
    [customerStore, hrmsStore],
  );

  useEffect(() => {
    if (!canViewHrms) return;
    void fetchCustomers(debouncedCustomerQuery.trim());
  }, [debouncedCustomerQuery, fetchCustomers, canViewHrms]);

  // URL deep-links only carry the customer id — swap in the real name once known.
  useEffect(() => {
    const selected = selectedCustomers[0];
    if (!selected || !String(selected.value).startsWith("Customer #")) return;
    const match =
      customerOptions.find(
        (option) => String(option.id) === String(selected.id),
      )?.value ||
      hrmsStore.employees.find(
        (employee) => String(employee.customer_id) === String(selected.id),
      )?.customer_name;
    if (match) setSelectedCustomers([{ id: selected.id, value: match }]);
  }, [customerOptions, hrmsStore.employees, selectedCustomers]);

  const customerList = useMemo(
    () => pinSelectedFirst(customerOptions, selectedCustomers),
    [customerOptions, selectedCustomers],
  );

  const fetchCampaigns = useCallback(
    (searchText: string, customerId: number) =>
      hrmsStore.getCampaignOptions(customerId, searchText),
    [hrmsStore],
  );

  useEffect(() => {
    if (!canViewHrms) return;
    const query = hrmsStore.campaignQuery.trim();
    if (query.length === 1 && !/^\d+$/.test(query)) return;
    void fetchCampaigns(query, draftCustomerId);
  }, [fetchCampaigns, canViewHrms, draftCustomerId, hrmsStore]);

  const scheduleCampaignSearch = (raw: string) => {
    hrmsStore.queueCampaignQuery(raw, (query) => {
      if (query.length === 1 && !/^\d+$/.test(query)) return;
      void fetchCampaigns(query, draftCustomerId);
    });
  };

  const campaignOptions = useMemo(
    () =>
      hrmsStore.campaignOptionRows
        .filter((row) => row.id != null && row.name)
        .map((row) => ({
          id: String(row.id),
          value: formatCampaignLabel(
            row.id,
            row.name,
            draftCustomerId > 0 ? null : row.customer_name,
          ),
        })),
    [hrmsStore.campaignOptionRows, draftCustomerId],
  );

  useEffect(() => {
    const selected = hrmsStore.selectedCampaigns[0];
    if (!selected || !String(selected.value).startsWith("Campaign #")) return;
    const match = campaignOptions.find(
      (option) => String(option.id) === String(selected.id),
    );
    if (match) hrmsStore.setSelectedCampaigns([match]);
  }, [campaignOptions, hrmsStore, hrmsStore.selectedCampaigns]);

  const campaignList = useMemo(
    () => pinSelectedFirst(campaignOptions, hrmsStore.selectedCampaigns),
    [campaignOptions, hrmsStore.selectedCampaigns],
  );

  const handleCustomerSelect = (items: FilterValue[]) => {
    const parsed = parseInt(String(items[0]?.id ?? ""), 10);
    const customerId = Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
    const customerChanged = draftCustomerId !== customerId;
    if (customerChanged) {
      hrmsStore.setSelectedCampaigns([]);
      hrmsStore.clearCampaignQuery();
    }
    setSelectedCustomers(items);
    setParams((prev) =>
      prev.customer_id === customerId && !customerChanged
        ? prev
        : {
            ...prev,
            page_no: 1,
            customer_id: customerId,
            ...(customerChanged ? { campaign_id: undefined } : {}),
          },
    );
  };

  const handleShipmentSelect = (items: FilterValue[]) => {
    const id = String(items[0]?.id ?? "all");
    const next = id === "yes" ? true : id === "no" ? false : undefined;
    setParams((prev) =>
      prev.has_shipment === next
        ? prev
        : { ...prev, page_no: 1, has_shipment: next },
    );
  };

  const handleCampaignSelect = (items: FilterValue[]) => {
      const parsed = parseInt(String(items[0]?.id ?? ""), 10);
      const campaignId =
        Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
      hrmsStore.setSelectedCampaigns(items);
      setParams((prev) =>
        prev.campaign_id === campaignId
          ? prev
          : { ...prev, page_no: 1, campaign_id: campaignId },
      );
  };

  const handleSortBySelect = (items: FilterValue[]) => {
      const id = String(items[0]?.id ?? "");
      const field = isSortField(id) ? id : DEFAULT_SORT_BY;
      setParams((prev) =>
        prev.sort_by === field ? prev : { ...prev, page_no: 1, sort_by: field },
      );
  };

  const handleSortTypeSelect = (items: FilterValue[]) => {
    try {
      const nextType =
        items[0]?.id === SortType.ASC ? SortType.ASC : SortType.DESC;
      setParams((prev) =>
        prev.sort_type === nextType
          ? prev
          : { ...prev, page_no: 1, sort_type: nextType },
      );
    } catch (error) {
      console.error("HrmsList.handleSortTypeSelect - Error:", error);
    }
  };

  const handleApply = () => {
    hrmsStore.clearFilterTimers();
    const searchText = hrmsStore.searchDraft.trim();
    const contactNumber = hrmsStore.contactDraft.trim();
    hrmsStore.searchDraft = searchText;
    hrmsStore.contactDraft = contactNumber;
    if (hrmsStore.searchInput) hrmsStore.searchInput.value = searchText;
    if (hrmsStore.contactInput) hrmsStore.contactInput.value = contactNumber;
    const campaignParsed = parseInt(
      String(hrmsStore.selectedCampaigns[0]?.id ?? ""),
      10,
    );
    const campaignId =
      Number.isFinite(campaignParsed) && campaignParsed > 0
        ? campaignParsed
        : undefined;
    setParams((prev) => {
      const sortBy =
        prev.sort_by && getSortFieldsForTab(selectedStatusTab).includes(prev.sort_by)
          ? prev.sort_by
          : DEFAULT_SORT_BY;
      return {
        ...prev,
        page_no: 1,
        search_text: searchText,
        contact_number: contactNumber || undefined,
        customer_id: draftCustomerId,
        campaign_id: campaignId,
        sort_by: sortBy,
      };
    });
  };

  const handleClearAll = () => {
      hrmsStore.clearFilterTimers();
      hrmsStore.searchDraft = "";
      hrmsStore.contactDraft = "";
      if (hrmsStore.searchInput) hrmsStore.searchInput.value = "";
      if (hrmsStore.contactInput) hrmsStore.contactInput.value = "";
      setSelectedCustomers([]);
      setCustomerQuery("");
      hrmsStore.setSelectedCampaigns([]);
      hrmsStore.clearCampaignQuery();
      void fetchCampaigns("", 0);
      setParams((prev) => ({
        ...prev,
        page_no: 1,
        search_text: "",
        contact_number: undefined,
        has_shipment: undefined,
        campaign_id: undefined,
        customer_id: 0,
        sort_by: DEFAULT_SORT_BY,
        sort_type: DEFAULT_SORT_TYPE,
      }));
  };

  const handleSortChange = (field: HrmsEmployeeSortField) => {
    const nextType =
      params.sort_by === field && params.sort_type === SortType.DESC
        ? SortType.ASC
        : SortType.DESC;
    setParams((prev) => ({
      ...prev,
      page_no: 1,
      sort_by: field,
      sort_type: nextType,
    }));
  };

  const handleStatusTabChange = (tab: HrmsStatusTab) => {
    setSelectedStatusTab(tab);
    const allowed = getSortFieldsForTab(tab);
    const keepSort = !!params.sort_by && allowed.includes(params.sort_by);
    setParams((prev) => ({
      ...prev,
      page_no: 1,
      ...getStatusParams(tab),
      ...(keepSort ? {} : { sort_by: DEFAULT_SORT_BY }),
    }));
  };

  const handleNewDaysChange = (items: FilterValue[]) => {
    const parsed = parseInt(String(items[0]?.id ?? ""), 10);
    setParams((prev) => ({
      ...prev,
      page_no: 1,
      ...getStatusParams("NEW", Number.isFinite(parsed) ? parsed : undefined),
    }));
  };

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setParams((prev) => ({ ...prev, page_no: newPage }));
  };

  const handleRowsChange = (newLimit: number) => {
    setParams((prev) => ({ ...prev, limit: newLimit, page_no: 1 }));
  };

  const countFor = (tab: HrmsStatusTab) => {
    if (hrmsStore.isLoadingCounts) return "…";
    if (tab === "ACTIVE") return hrmsStore.counts.active;
    if (tab === "INACTIVE") return hrmsStore.counts.inactive;
    return hrmsStore.counts.new_employees;
  };

  const renderHeader = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        justifyContent: "space-between",
        alignItems: { xs: "flex-start", md: "center" },
        gap: 2,
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <CustomTypography
          variant="heading3"
          color={colors.title}
          sx={{ letterSpacing: "-0.01em" }}
        >
          HRMS Employees
        </CustomTypography>
        <CustomTypography
          variant="body2"
          color={
            colors.isDark
              ? colors.subtitle
              : theme.palette.customColors.greyTextColor
          }
        >
          Employees synced from customer HR systems.
        </CustomTypography>
      </Box>
      {canViewHrms && (
        <Chip
          size="small"
          label={`Total employees: ${hrmsStore.isLoadingCounts ? "…" : hrmsStore.counts.total}`}
          sx={pillSx(theme, "info")}
        />
      )}
    </Box>
  );

  const paperSx = {
    mt: 3,
    p: { xs: 2, md: 3 },
    borderRadius: 2,
    bgcolor: colors.surface,
    boxShadow:
      "rgba(0, 0, 0, 0.15) 0px 4px 24px, rgba(0, 0, 0, 0.05) 0px 8px 32px",
  };

  if (!canViewHrms) {
    return (
      <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }} sx={{ px: 2 }}>
        <Paper sx={paperSx}>{renderHeader()}</Paper>
        <PermissionBlockLabel title="HRMS" />
      </Grid2>
    );
  }

  const renderIconLine = (
    icon: React.ReactNode,
    content: React.ReactNode,
    muted = false,
  ) => (
    <Box
      sx={{ display: "flex", alignItems: "flex-start", gap: 0.75, minWidth: 0 }}
    >
      <Box
        sx={{
          color: colors.icon,
          display: "flex",
          pt: "1px",
          "& svg": { fontSize: 14 },
        }}
      >
        {icon}
      </Box>
      <CustomTypography
        variant="body3"
        color={muted ? colors.subtitle : colors.title}
        sx={{ fontSize: "0.75rem", wordBreak: "break-word", minWidth: 0 }}
      >
        {content}
      </CustomTypography>
    </Box>
  );

  const renderProfileChips = (employee: HrmsEmployee) => {
    const fields = getProfileFields(employee).filter(
      (field) =>
        field.value !== null &&
        field.value !== undefined &&
        String(field.value).trim() !== "",
    );
    if (!fields.length) {
      return (
        <CustomTypography variant="body3" color={colors.subtitle}>
          -
        </CustomTypography>
      );
    }
    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
        {fields.map((field) => (
          <Chip
            key={field.label}
            size="small"
            label={`${field.label}: ${field.value}`}
            sx={{
              ...pillSx(
                theme,
                field.label === "T-shirt" ? "violet" : "neutral",
              ),
              maxWidth: "100%",
            }}
          />
        ))}
      </Box>
    );
  };

  const renderEmployeeIdentity = (employee: HrmsEmployee) => (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0 }}
    >
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
      >
        <CustomTypography
          variant="heading8"
          color={colors.title}
          sx={{ fontSize: "0.85rem", wordBreak: "break-word" }}
        >
          {getDisplayName(employee)}
        </CustomTypography>
        <Chip
          size="small"
          label={employee.employment_status || "-"}
          sx={pillSx(theme, getStatusTone(employee.employment_status))}
        />
        <Chip
          size="small"
          icon={<LocalShippingOutlined />}
          label={isShipmentCreated(employee) ? "Shipment created" : "No shipment"}
          sx={{
            ...pillSx(theme, isShipmentCreated(employee) ? "info" : "neutral"),
            "& .MuiChip-icon": { fontSize: 14, color: "inherit", ml: 0.75, mr: -0.25 },
          }}
        />
        {renderCampaigns(employee)}
      </Box>
      <CustomTypography
        variant="body3"
        color={colors.subtitle}
        sx={{ fontSize: "0.72rem" }}
      >
        Emp No. {employee.employee_number || "-"}
      </CustomTypography>
      {renderIconLine(
        <BusinessOutlined />,
        employee.customer_name || "-",
        true,
      )}
    </Box>
  );

  const renderContact = (employee: HrmsEmployee) => (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: 0.75, minWidth: 0 }}
    >
      <EmailCopyRow
        email={employee.work_email}
        color={colors.title}
        iconColor={colors.icon}
      />
      {renderIconLine(<PhoneOutlined />, getPhoneNumber(employee))}
    </Box>
  );

  const renderAddress = (employee: HrmsEmployee) => {
    const pincode = getPincode(employee);
    return (
      <Box
        sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0 }}
      >
        {renderIconLine(
          <LocationOnOutlined />,
          formatAddress(pickAddress(employee.addresses)),
        )}
        {pincode && (
          <CustomTypography
            variant="body3"
            color={colors.subtitle}
            sx={{ fontSize: "0.72rem", pl: 2.75 }}
          >
            PIN {pincode}
          </CustomTypography>
        )}
      </Box>
    );
  };

  const formatDateCell = (value?: string | null) =>
    value ? formatDateWithoutTime(value) : "-";

  const headerCellSx = {
    fontSize: "0.75rem",
    fontWeight: 700,
    color: colors.title,
    backgroundColor: colors.headerBg,
    borderBottom: `1px solid ${colors.border}`,
    py: 1.5,
    px: 1.5,
    lineHeight: 1.2,
    whiteSpace: "nowrap" as const,
  };

  const bodyCellSx = {
    fontSize: "0.75rem",
    color: colors.title,
    borderBottom: `1px solid ${colors.border}`,
    py: 2,
    px: 1.5,
    verticalAlign: "top" as const,
  };

  const renderSortableHeader = (
    field: HrmsEmployeeSortField,
    width: string,
  ) => (
    <TableCell key={field} sx={{ ...headerCellSx, width, textAlign: "center" }}>
      <TableSortLabel
        active={params.sort_by === field}
        direction={
          params.sort_by === field && params.sort_type === SortType.ASC
            ? "asc"
            : "desc"
        }
        onClick={() => handleSortChange(field)}
        sx={{
          color: `${colors.title} !important`,
          "& .MuiTableSortLabel-icon": {
            color: `${colors.accent} !important`,
          },
        }}
      >
        {SORT_FIELD_LABELS[field]}
      </TableSortLabel>
    </TableCell>
  );

  const renderCampaigns = (employee: HrmsEmployee) => {
    const campaigns = (employee.campaigns ?? []).filter((campaign) => campaign.name);
    if (campaigns.length === 0) return null;
    return campaigns.map((campaign) => (
      <Chip
        key={campaign.id}
        size="small"
        label={formatCampaignLabel(campaign.id, campaign.name)}
        sx={{ ...pillSx(theme, "violet"), maxWidth: "100%" }}
      />
    ));
  };

  const showTermination = selectedStatusTab === "INACTIVE";
  const columnWidths = showTermination
    ? {
        employee: "24%",
        contact: "18%",
        address: "18%",
        profile: "18%",
        date: "7.33%",
      }
    : {
        employee: "26%",
        contact: "20%",
        address: "20%",
        profile: "18%",
        date: "8%",
      };

  const renderTable = () => (
    <Table sx={{ tableLayout: "fixed", width: "100%" }}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ ...headerCellSx, width: columnWidths.employee }}>
            Employee
          </TableCell>
          <TableCell sx={{ ...headerCellSx, width: columnWidths.contact }}>
            Contact
          </TableCell>
          <TableCell sx={{ ...headerCellSx, width: columnWidths.address }}>
            Address
          </TableCell>
          <TableCell sx={{ ...headerCellSx, width: columnWidths.profile }}>
            Custom Fields
          </TableCell>
          {sortFields.map((field) =>
            renderSortableHeader(field, columnWidths.date),
          )}
        </TableRow>
      </TableHead>
      <TableBody>
        {hrmsStore.employees.map((employee) => (
          <TableRow
            key={employee.id}
            sx={{
              "&:hover": { backgroundColor: colors.rowHover },
              "&:last-child td": { borderBottom: 0 },
            }}
          >
            <TableCell sx={bodyCellSx}>
              {renderEmployeeIdentity(employee)}
            </TableCell>
            <TableCell sx={bodyCellSx}>{renderContact(employee)}</TableCell>
            <TableCell sx={bodyCellSx}>{renderAddress(employee)}</TableCell>
            <TableCell sx={bodyCellSx}>
              {renderProfileChips(employee)}
            </TableCell>
            {sortFields.map((field) => (
              <TableCell
                key={field}
                sx={{ ...bodyCellSx, textAlign: "center" }}
              >
                {formatDateCell(employee[field])}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  const renderCards = () => (
    <Grid2 container spacing={2} sx={{ p: { xs: 1.5, sm: 2 } }}>
      {hrmsStore.employees.map((employee) => (
        <Grid2 key={employee.id} size={{ xs: 12, md: 6 }}>
          <Paper
            variant="outlined"
            sx={{
              height: "100%",
              p: 2,
              borderRadius: 2,
              borderColor: colors.softBorder,
              bgcolor: colors.softSurface,
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              transition: "border-color 0.2s ease",
              "&:hover": { borderColor: colors.accent },
            }}
          >
            {renderEmployeeIdentity(employee)}
            <Divider sx={{ borderColor: colors.border }} />
            {renderContact(employee)}
            {renderAddress(employee)}
            {renderProfileChips(employee)}
            <Box
              sx={{
                mt: "auto",
                pt: 1.5,
                borderTop: `1px dashed ${colors.border}`,
                display: "grid",
                gridTemplateColumns: `repeat(${sortFields.length}, minmax(0, 1fr))`,
                gap: 1,
              }}
            >
              {sortFields.map((field) => (
                <Box key={field} sx={{ minWidth: 0 }}>
                  <CustomTypography
                    variant="heading9"
                    color={colors.label}
                    sx={{
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                      color:
                        params.sort_by === field ? colors.accent : colors.label,
                    }}
                  >
                    {SORT_FIELD_LABELS[field]}
                  </CustomTypography>
                  <CustomTypography
                    variant="body3"
                    color={colors.title}
                    sx={{ fontSize: "0.75rem", mt: 0.25 }}
                  >
                    {formatDateCell(employee[field])}
                  </CustomTypography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid2>
      ))}
    </Grid2>
  );

  return (
    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }} sx={{ px: 2 }}>
      <Paper sx={paperSx}>
        {renderHeader()}

        <Paper
          variant="outlined"
          sx={{
            mt: 2,
            borderRadius: 2,
            borderColor: colors.border,
            bgcolor: colors.surface,
            overflow: "hidden",
          }}
        >
          <Box
            onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 2,
              py: 0.5,
              cursor: "pointer",
              borderBottom: isFiltersExpanded
                ? `1px solid ${colors.border}`
                : "none",
              "&:hover": { backgroundColor: theme.palette.action.hover },
            }}
          >
            <CustomTypography variant="heading6" color={colors.title}>
              Search & Filters
            </CustomTypography>
            <IconButton size="small">
              {isFiltersExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
          <Collapse in={isFiltersExpanded} timeout="auto" unmountOnExit>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "flex-end",
                gap: 1.5,
                p: 2,
              }}
            >
              <Box sx={{ width: { xs: "100%", sm: "calc(50% - 6px)", md: 280 }, maxWidth: { md: 320 }, minWidth: 0 }}>
                <FormTextField
                  marginBottom={0}
                  id="hrms-search"
                  placeholder="Search by name, email or employee number"
                  type="search"
                  variant="outlined"
                  inputRef={(node: HTMLInputElement | null) => {
                    hrmsStore.searchInput = node;
                    if (node) node.value = hrmsStore.searchDraft;
                  }}
                  onChange={(_, value) => {
                    hrmsStore.queueSearchDraft(value || "", (searchText) => {
                      setParams((prev) =>
                        (prev.search_text || "") === searchText
                          ? prev
                          : { ...prev, page_no: 1, search_text: searchText },
                      );
                    });
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") handleApply();
                  }}
                />
              </Box>
              <Box sx={{ width: { xs: "100%", sm: "calc(50% - 6px)", md: 168 }, maxWidth: { md: 190 }, minWidth: 0 }}>
                <FormTextField
                  marginBottom={0}
                  id="hrms-contact-number"
                  placeholder="Contact number"
                  type="search"
                  variant="outlined"
                  inputRef={(node: HTMLInputElement | null) => {
                    hrmsStore.contactInput = node;
                    if (node) node.value = hrmsStore.contactDraft;
                  }}
                  onChange={(_, value) => {
                    hrmsStore.queueContactDraft(value || "", (nextContact) => {
                      setParams((prev) =>
                        (prev.contact_number || "") === nextContact
                          ? prev
                          : {
                              ...prev,
                              page_no: 1,
                              contact_number: nextContact || undefined,
                            },
                      );
                    });
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") handleApply();
                  }}
                />
              </Box>
              <Box sx={{ width: { xs: "100%", sm: "calc(50% - 6px)", md: 156 }, maxWidth: { md: 170 }, minWidth: 0 }}>
                <AppSearchDropdownMulti
                  title="Customer"
                  isMultiSelect={false}
                  floatingLabel
                  value={selectedCustomers}
                  list={customerList}
                  local={false}
                  error={!!customerError}
                  errorMessage={customerError ?? undefined}
                  handleSearch={(text) => setCustomerQuery(text ?? "")}
                  handleSelect={handleCustomerSelect}
                />
              </Box>
              <Box sx={{ width: { xs: "100%", sm: "calc(50% - 6px)", md: 140 }, maxWidth: { md: 150 }, minWidth: 0 }}>
                <AppSearchDropdownMulti
                  title="Shipment"
                  isMultiSelect={false}
                  floatingLabel
                  local
                  list={SHIPMENT_OPTIONS}
                  value={selectedShipment}
                  handleSelect={handleShipmentSelect}
                />
              </Box>
              <Box sx={{ width: { xs: "100%", sm: "calc(50% - 6px)", md: 176 }, maxWidth: { md: 200 }, minWidth: 0 }}>
                <AppSearchDropdownMulti
                  title="Campaign"
                  isMultiSelect={false}
                  floatingLabel
                  value={hrmsStore.selectedCampaigns}
                  list={campaignList}
                  local={false}
                  error={!!hrmsStore.campaignOptionsError}
                  errorMessage={hrmsStore.campaignOptionsError ?? undefined}
                  handleSearch={(text) => scheduleCampaignSearch(text ?? "")}
                  handleSelect={handleCampaignSelect}
                />
              </Box>
              <Box sx={{ width: { xs: "100%", sm: "calc(50% - 6px)", md: 148 }, maxWidth: { md: 160 }, minWidth: 0 }}>
                <AppSearchDropdownMulti
                  title="Sort By"
                  isMultiSelect={false}
                  floatingLabel
                  local
                  list={sortFieldOptions}
                  value={selectedSortBy}
                  handleSelect={handleSortBySelect}
                />
              </Box>
              <Box sx={{ width: { xs: "100%", sm: "calc(50% - 6px)", md: 140 }, maxWidth: { md: 150 }, minWidth: 0 }}>
                <AppSearchDropdownMulti
                  title="Order"
                  isMultiSelect={false}
                  floatingLabel
                  local
                  list={SORT_ORDER_OPTIONS}
                  value={selectedSortOrder}
                  handleSelect={handleSortTypeSelect}
                />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  width: { xs: "100%", sm: "calc(50% - 6px)", md: "auto" },
                }}
              >
                <AppButton
                  variant="transparentPrimary"
                  label="Clear All"
                  onClick={handleClearAll}
                  customStyles={{
                    minWidth: { xs: 0, sm: 104 },
                    height: 37,
                    flex: { xs: 1, sm: "0 0 auto" },
                  }}
                />
                <AppButton
                  variant="primary"
                  label="Apply"
                  onClick={handleApply}
                  customStyles={{
                    minWidth: { xs: 0, sm: 96 },
                    height: 37,
                    flex: { xs: 1, sm: "0 0 auto" },
                  }}
                />
              </Box>
            </Box>
          </Collapse>
        </Paper>

        <Box
          sx={{
            mt: 2,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "flex-end" },
            justifyContent: "space-between",
            gap: 1.5,
            borderBottom: `1px solid ${colors.border}`,
          }}
        >
          <Tabs
            value={selectedStatusTab}
            onChange={(_, value: HrmsStatusTab) => handleStatusTabChange(value)}
            variant="scrollable"
            scrollButtons="auto"
            sx={(t) => ({ ...listingStatusTabsStyles(t), borderBottom: 0 })}
          >
            {HRMS_STATUS_TABS.map((tab) => (
              <Tab
                key={tab.id}
                value={tab.id}
                label={`${tab.label.toUpperCase()}${tab.id === "NEW" ? ` · ${params.new_days ?? DEFAULT_NEW_DAYS}D` : ""} (${countFor(tab.id)})`}
              />
            ))}
          </Tabs>
          {selectedStatusTab === "NEW" && (
            <Box sx={{ width: { xs: "100%", sm: 220 }, pb: 1 }}>
              <AppSearchDropdownMulti
                title="Joined In"
                isMultiSelect={false}
                floatingLabel
                local
                list={pinSelectedFirst(NEW_DAY_OPTIONS, selectedNewDays)}
                value={selectedNewDays}
                handleSelect={handleNewDaysChange}
              />
            </Box>
          )}
        </Box>

        {hrmsStore.errorMessage && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {hrmsStore.errorMessage}
          </Alert>
        )}

        <Paper
          sx={{
            mt: 2,
            borderRadius: 2,
            border: `1px solid ${colors.border}`,
            boxShadow: `0 2px 8px ${theme.palette.customColors.shadowLightBorder}`,
            bgcolor: colors.surface,
            overflow: "hidden",
          }}
        >
          {hrmsStore.isLoadingList ? (
            <Box
              sx={{
                minHeight: "20rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress />
            </Box>
          ) : hrmsStore.employees.length === 0 ? (
            <Box
              sx={{
                m: 2,
                minHeight: "10rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                border: `1px dashed ${colors.border}`,
                borderRadius: 2,
              }}
            >
              <CustomTypography
                variant="heading6"
                color={
                  colors.isDark
                    ? colors.subtitle
                    : theme.palette.customColors.greyTextColor
                }
              >
                No employees found for selected filters.
              </CustomTypography>
            </Box>
          ) : isTableLayout ? (
            renderTable()
          ) : (
            renderCards()
          )}
          <Box sx={{ borderTop: `1px solid ${colors.border}` }}>
            <AppTablePagenation
              fontSize="12px"
              totalCount={hrmsStore.total_count}
              limit={params.limit}
              pageNo={params.page_no}
              onPageChange={handlePageChange}
              handleRowsChange={handleRowsChange}
            />
          </Box>
        </Paper>
      </Paper>
    </Grid2>
  );
};

export default observer(HrmsList);
